import axios from 'axios';
import { toast } from 'sonner'

import { CONFIG } from 'src/config-global';
import { logout, getAccessToken, setAccessToken, getActiveSessionId } from 'src/stores/user-session';

export { getAccessToken };
// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: CONFIG.site.serverUrl,
  withCredentials: true,
});

// Function to refresh the access token
export const refreshAccessToken = async () => {
  try {
    // The refresh token is an HTTP-only cookie.
    // The backend uses X-Session-ID to identify which refresh cookie to use.
    const res = await axiosInstance.post(
      endpoints.auth.refreshToken,
      {},
    );

    const { access } = res.data;

    if (access) {
      setAccessToken(access);
      return access;
    }

    return null;
  } catch (error) {
    // Handle refresh token error or redirect to login
    if (error.response?.status === 401) {
      // Only show toast if we actually had a session we were trying to refresh
      if (getActiveSessionId()) {
        toast.error('Session expired, please log in again');
        setTimeout(() => {
          logout(); // This clears the session from the store
          window.location.reload();
        }, 3000);
      }
    } else {
      // generic error
      // toast.error('Failed to refresh session');
    }
    throw error;
  }
};

axiosInstance.interceptors.request.use((config) => {
  const sessionId = getActiveSessionId();
  if (sessionId) {
    config.headers['X-Session-ID'] = sessionId;

    // Attach Bearer token if available in memory
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return {
    ...config,
  }
});


// Add a response interceptor to refresh the access token if the request fails with 401
axiosInstance.interceptors.response.use(
  (response) => response,
  async (originalError) => {
    const originalRequest = originalError.config;

    if (originalError.code === "ERR_NETWORK") {
      if (!originalRequest?._skipNetworkErrorToast) {
        toast.error('Network error, please check your internet connection');
      }
      return Promise.reject(originalError);
    }

    // When responseType is 'blob', error responses with JSON bodies are
    // returned as Blob objects. Parse them back to JSON so the interceptor
    // can read fields like `code` and `detail`.
    if (originalError.response?.data instanceof Blob) {
      try {
        const text = await originalError.response.data.text();
        originalError.response.data = JSON.parse(text);
      } catch (_) {
        // Not valid JSON — leave data as-is
      }
    }

    // if response is not JSON, try to parse it as text to extract error details
    if (originalError.response?.data && typeof originalError.response.data !== 'object') {
      try {
        originalError.response.data = JSON.parse(originalError.response.data);
      } catch (_) {
        // Not valid JSON — leave data as-is
      }
    }

    // If the error status is 401 and there is no originalRequest._retry flag,
    // and response.code is "token_not_valid" or "not_authenticated",
    // it means the token has expired and we need to refresh it
    if (
      originalError.response?.status === 401 &&
      (originalError.response?.data?.code === "token_not_valid" || originalError.response?.data?.code === "not_authenticated") &&
      !originalRequest._retry &&
      originalRequest.url !== endpoints.auth.refreshToken
    ) {

      // Only attempt refresh if we actually have a session
      const sessionId = getActiveSessionId();
      if (!sessionId) {
        return Promise.reject(originalError);
      }

      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();

        // Retry the original request

        // Ensure headers are updated on the retry request
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        if (sessionId) {
          originalRequest.headers['X-Session-ID'] = sessionId;
        }

        return axiosInstance(originalRequest);
      } catch (error) {
        return Promise.reject(error);
      }
    }

    return Promise.reject(originalError);
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  return (await axiosInstance.get(url, { ...config })).data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  auth: {
    signIn: '/auth/sign-in',
    signUp: '/auth/sign-up',
    requestAccessToken: '/auth/token',
    refreshToken: '/auth/token/refresh',
    logout: '/auth/logout',
    logoutAll: '/auth/logout/all',
  },
  me: {
    details: '/me',
    stores: '/me/stores',
    brands: '/me/brands',
    roles: '/me/roles',
    orgTypes: '/me/org-types',
    paycard: {
      employees: '/me/paycard/employees',
    },
    inStoreReports: {
      details: id => `/me/instore-reports/${id}`,
      list: '/me/instore-reports',
      generate: id => `/me/instore-reports/${id}/generate`,
    },
    inStoreReportsCategories: {
      list: '/me/instore-report-categories',
    },
    inStoreReportsSchedules: {
      list: '/me/instore-report-schedules',
      details: id => `/me/instore-report-schedules/${id}`,
      runNow: id => `/me/instore-report-schedules/${id}/run`,
    },
  },
  account: {
    requestPasswordReset: '/account/request-password-reset',
    resetPassword: '/account/reset-password',
    validateResetPasswordToken: '/account/validate-reset-password-token'
  },
  paycard: {
    configs: '/cid/paycard/configs',
    rapidcards: '/cid/paycard/rapid-cards',
    rapidConfig: '/cid/paycard/rapid-configs',
    rapidDisbursementConfigs: '/cid/paycard/rapid-disbursement-configs',
    akimboDistributorMap: '/cid/paycard/akimbo-distributor-maps',
    payments: {
      list: '/cid/paycard/shifts',
      count: '/cid/paycard/shifts/count',
      markAsManual: '/cid/paycard/shifts/mark-as-manual',
      requeue: '/cid/paycard/shifts/requeue',
      optOut: '/cid/paycard/shifts/opt-out',
    },
    employees: {
      list: '/cid/paycard/employees',
      count: '/cid/paycard/employees/count',
      assignPayments: id => `/cid/paycard/employees/${id}/assign-payments`,
      optOut: id => `/cid/paycard/employees/${id}/opt-out`,
      checkAccountNumberStatus: '/cid/paycard/employees/check-account-number-status',
      editAccount: id => `/cid/paycard/employees/${id}/edit-account`,
      registerAccount: id => `/cid/paycard/employees/${id}/register-account`,
      checkAccountStatus: id => `/cid/paycard/employees/${id}/account-status`,
    },
  },
  c: {
    datasets: '/cid/c-datasets',
    users: '/cid/c-users',
    currentUser: '/cid/me',
    stores: '/cid/c-stores',
    roles: '/cid/c-roles',
    inStoreReports: {
      list: '/cid/instore-reports',
      generate: id => `/cid/instore-reports/${id}/generate`,
      details: id => `/cid/instore-reports/${id}`,
    },
    inStoreReportCategories: {
      list: '/cid/instore-report-categories',
    },
    inStoreReportSchedules: {
      list: '/cid/instore-report-schedules',
      details: id => `/cid/instore-report-schedules/${id}`,
      runNow: id => `/cid/instore-report-schedules/${id}/run`,
    },
    coupons: '/cid/coupons',
  },
  h: {
    countries: '/cid/h-countries',
    states: '/cid/h-states',
    timezones: '/cid/h-timezones',
  },
  bank: {
    list: '/cid/banks',
  },
  org: {
    types: '/cid/org-types',
    managers: '/cid/org-managers',
    companies: '/cid/org-companies',
    brands: '/cid/org-brands',
  },
  knowledge: {
    llmConfig: '/knowledge/llm-config',
    query: '/knowledge/query',
    title: '/knowledge/title',
    cancel: '/knowledge/cancel',
    conversations: (conversationType) =>
      conversationType
        ? `/knowledge/conversations?conversation_type=${encodeURIComponent(conversationType)}`
        : '/knowledge/conversations',
    conversationDetails: (id) => `/knowledge/conversations/${id}`,
    conversationQuery: '/knowledge/conversations/query',
    messageFeedback: (messageId) => `/knowledge/messages/${messageId}/feedback`,
    attachmentDownload: (messageId, key) =>
      `/knowledge/messages/${messageId}/attachments/download?key=${encodeURIComponent(key)}`,
    usageMe: '/knowledge/usage/me',
    usageConversation: (conversationId) => `/knowledge/usage/conversation/${conversationId}`,
    usageOrg: '/knowledge/usage/org',
    reportContext: (reportUri) =>
      `/knowledge/report-contexts?report_uri=${encodeURIComponent(reportUri)}`,
    reportContexts: '/knowledge/report-contexts',
    exampleQuestions: '/knowledge/example-questions',
  },
  aboveStore: {
    acct: {
      amounts: {
        setup: brand => `/above-store/acct/amounts/${brand}/setup`,
        data: brand => `/above-store/acct/amounts/${brand}/data`,
        update: brand => `/above-store/acct/amounts/${brand}/update`,
        save: brand => `/above-store/acct/amounts/${brand}/save`,
      },
    },
  },
  envysion: {
    cameraVideo: sensorId => `/envysion/cameras/${sensorId}/video`,
  },
};
